import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { realtime } from './connection';
import { RealtimeEventType, RealtimeMessage } from './types';
import {
  LIVE_POLL_MS,
  FALLBACK_POLL_MS,
  selectPollInterval,
  type PollIntervalOptions,
} from './poll-interval';

/** Real-time is on by default; set VITE_ENABLE_REALTIME=false to force polling. */
const REALTIME_ENABLED = import.meta.env.VITE_ENABLE_REALTIME !== 'false';

// Re-exported so pages can import the cadence constants from one place.
export { LIVE_POLL_MS, FALLBACK_POLL_MS };

const RealtimeContext = createContext<boolean>(false);

/**
 * Owns the single SignalR connection lifecycle: starts it once the user is
 * authenticated, tears it down on logout. Mount inside AuthProvider.
 */
export function RealtimeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  useEffect(() => {
    if (!REALTIME_ENABLED || !user) return;
    void realtime.start();
    return () => {
      // Full stop only on sign-out (user becomes null), handled by deps below.
    };
  }, [user]);

  useEffect(() => {
    if (!user) void realtime.stop();
  }, [user]);

  return <RealtimeContext.Provider value={REALTIME_ENABLED}>{children}</RealtimeContext.Provider>;
}

export function useRealtimeEnabled(): boolean {
  return useContext(RealtimeContext);
}

/**
 * Re-renders the caller whenever the connection/group state changes and reports
 * whether push is *actually live* for `group` (socket connected AND subscribed).
 * Returns false when real-time is disabled or no group is given.
 */
export function useRealtimeLive(group: string | null): boolean {
  const enabled = useRealtimeEnabled();
  const subscribe = useCallback((cb: () => void) => realtime.onStateChange(cb), []);
  const getSnapshot = useCallback(
    () => (enabled && !!group ? realtime.isLive(group) : false),
    [enabled, group],
  );
  return useSyncExternalStore(subscribe, getSnapshot);
}

/**
 * Adaptive poll interval for a group: the slow `liveMs` while push is live, the
 * fast `fallbackMs` when it isn't (Azure unconfigured, connecting, reconnecting,
 * dropped, or this group's subscribe failed).
 */
export function useAdaptivePollInterval(
  group: string | null,
  opts?: PollIntervalOptions,
): number {
  const live = useRealtimeLive(group);
  return selectPollInterval(live, opts);
}

type RealtimeHandlers = Partial<Record<RealtimeEventType, (msg: RealtimeMessage) => void>>;

/**
 * Subscribe to a real-time group for the lifetime of the calling component and
 * dispatch incoming messages to per-event-type handlers.
 *
 * @param group    e.g. panelGroup(panelId), or null to disable (e.g. no id yet).
 * @param handlers map of event type -> callback (typically a query invalidation).
 * @returns `{ live }` — whether push is currently live for this group, so the
 *          page can choose an adaptive poll interval and catch up on reconnect.
 */
export function useRealtimeGroup(
  group: string | null,
  handlers: RealtimeHandlers,
): { live: boolean } {
  const enabled = useRealtimeEnabled();
  // Keep latest handlers without re-registering the listener every render.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!enabled || !group) return;

    let active = true;
    void realtime.start().then(() => {
      if (active) void realtime.subscribe(group);
    });

    const off = realtime.onMessage((msg) => {
      const handler = handlersRef.current[msg.type];
      if (handler) handler(msg);
    });

    return () => {
      active = false;
      off();
      void realtime.unsubscribe(group);
    };
  }, [enabled, group]);

  return { live: useRealtimeLive(group) };
}
